import { COMPANY, fmtMoney } from "./pricing";

const esc = (s) =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const MOWER_LABELS = {
  zero_turn: "Zero-Turn Mower",
  riding: "Riding Tractor",
  stand_on: "Stand-On Mower",
  compact_tractor: "Compact Tractor",
  utv: "UTV / Side-by-Side",
  golf_cart: "Golf Cart",
  implement: "Implement / Attachment",
  other: "Other",
};

// Customer-facing delivery quote: shows the delivery price only (no internal costs/margins).
export function buildQuoteHtml({ pickup, stops, effectiveMiles, schedule, units, salesAgent, customers, customer, mowerCost, price, methodLabel, stopSplits, stopEtas }) {
  const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const validStops = (stops || []).filter((s) => s && s.trim());
  const stopRows = validStops.map((s, idx) => `<li>Stop ${idx + 1}: ${esc(s)}</li>`).join("");

  // Multi-customer delivery split — always shown for multi-drop quotes so each
  // sales agent can communicate their client's share.
  const splitBlock =
    (stopSplits || []).length > 1
      ? `<h3 style="margin-top:22px">Delivery cost breakdown per customer / drop</h3>
        <table>
          <thead><tr><th>Customer</th><th>Delivery stop</th><th style="text-align:right">Share of delivery</th></tr></thead>
          <tbody>
            ${stopSplits
              .map(
                (sp) => `<tr>
                  <td>${esc(sp.customer || "—")}</td>
                  <td>${esc(sp.address)}</td>
                  <td style="text-align:right">${fmtMoney(sp.share)}${sp.pct != null ? ` <span style="color:#57534E;font-size:11px">(${sp.pct}%)</span>` : ""}</td>
                </tr>`
              )
              .join("")}
          </tbody>
        </table>
        <p class="muted" style="margin-top:4px">Split is proportional to the driving distance of each drop.</p>`
      : "";

  const customerList = (customers?.length ? customers : customer ? [customer] : []).filter(
    (c) => c && (c.name || c.address || c.email || c.phone)
  );
  const customerBlock = customerList.length
    ? `<div class="preparedfor">
        <div class="pf-label">Prepared for</div>
        ${customerList
          .map(
            (c) => `<div style="margin-bottom:6px">
              ${c.name ? `<div class="pf-name">${esc(c.name)}</div>` : ""}
              ${c.address ? `<div>${esc(c.address)}</div>` : ""}
              <div class="muted">${[c.phone, c.email].filter(Boolean).map(esc).join(" &middot; ")}</div>
              ${c.agent?.name ? `<div class="muted">K&amp;G Agent: ${esc(c.agent.name)}${c.agent.email ? ` (${esc(c.agent.email)})` : ""}</div>` : ""}
            </div>`
          )
          .join("")}
      </div>`
    : "";

  const mower = Number(mowerCost) || 0;
  const totalsBlock =
    mower > 0
      ? `<table style="margin-top:14px">
          <tbody>
            <tr><td><b>Equipment</b></td><td style="text-align:right">${fmtMoney(mower)}</td></tr>
            <tr><td><b>Delivery</b></td><td style="text-align:right">${fmtMoney(price)}</td></tr>
            <tr style="background:#FFF7ED"><td><b>Total (equipment + delivery)</b></td><td style="text-align:right"><b>${fmtMoney(mower + Number(price || 0))}</b></td></tr>
          </tbody>
        </table>`
      : "";

  // In-house estimated delivery schedule per drop (10h driving/day max).
  const etaBlock =
    (stopEtas || []).length > 0
      ? `<h3 style="margin-top:22px">Estimated delivery schedule (K&amp;G in-house truck)</h3>
        <table>
          <thead><tr><th>Stop</th><th>Customer</th><th>Delivery address</th><th style="text-align:right">Est. delivery</th></tr></thead>
          <tbody>
            ${stopEtas
              .map(
                (e, i) => `<tr>
                  <td>${i + 1}</td>
                  <td>${esc(e.customer || "—")}</td>
                  <td>${esc(e.address)}</td>
                  <td style="text-align:right"><b>${esc(e.date || `Day ${e.days} after pickup`)}</b></td>
                </tr>`
              )
              .join("")}
          </tbody>
        </table>
        <p class="muted" style="margin-top:4px">Based on a maximum of 10 driving hours per day. Dates are estimates and may shift with weather and traffic.</p>`
      : "";

  const unitRows = (units || [])
    .map(
      (u, idx) => `<tr>
      <td>${idx + 1}</td>
      <td>${esc(MOWER_LABELS[u.type] || u.type || "-")}</td>
      <td>${esc(u.brand || "-")}</td>
      <td>${esc(u.model || "-")}</td>
      <td style="text-align:center">${esc(u.qty || 1)}</td>
    </tr>`
    )
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"/>
<title>K&G Delivery Quote</title>
<style>
  * { box-sizing:border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color:#1C1917; margin:0; padding:40px; background:#fff; }
  .brandbar { background:#1C1917; color:#fff; padding:22px 26px; border-radius:10px; display:flex; justify-content:space-between; align-items:center; }
  .brandbar .meta { text-align:right; font-size:12px; color:#d6d3d1; line-height:1.5; }
  h2 { font-size:14px; text-transform:uppercase; letter-spacing:1px; color:#F97316; border-bottom:2px solid #F97316; padding-bottom:6px; margin:28px 0 12px; }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  th { background:#1C1917; color:#fff; text-align:left; padding:8px; font-size:11px; text-transform:uppercase; }
  td { border:1px solid #E7E5E4; padding:8px; }
  ul { margin:6px 0; padding-left:20px; font-size:13px; line-height:1.7; }
  .pricebox { margin-top:26px; padding:22px; background:#FFF7ED; border:2px solid #F97316; border-radius:10px; text-align:center; }
  .pricebox .amount { font-size:34px; font-weight:bold; color:#1C1917; }
  .pricebox .label { font-size:12px; text-transform:uppercase; letter-spacing:1px; color:#EA580C; font-weight:bold; }
  .muted { color:#57534E; font-size:12px; }
  .preparedfor { margin-top:20px; padding:14px 18px; border:1px solid #E7E5E4; border-left:4px solid #F97316; border-radius:8px; font-size:13px; line-height:1.6; }
  .preparedfor .pf-label { font-size:11px; text-transform:uppercase; letter-spacing:1px; color:#EA580C; font-weight:bold; }
  .preparedfor .pf-name { font-weight:bold; font-size:15px; }
  .footer { margin-top:26px; font-size:12px; color:#57534E; line-height:1.7; }
  @media print { body { padding:14px; } .brandbar { -webkit-print-color-adjust:exact; print-color-adjust:exact; } th{ -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
</style></head>
<body>
  <div class="brandbar">
    <div>
      <img src="${window.location.origin}/kg-logo.png" alt="K&amp;G Mowers + Tractors" style="height:54px;display:block;" />
      <div style="font-size:12px;color:#F97316;margin-top:8px;font-weight:bold;">Equipment Delivery Quote</div>
    </div>
    <div class="meta">
      <div><b style="color:#fff;">${esc(COMPANY.name)}</b></div>
      <div>${esc(COMPANY.address)}</div>
      <div>${esc(COMPANY.phone)}</div>
      <div>${esc(date)}</div>
    </div>
  </div>

  ${customerBlock}

  <div class="pricebox">
    <div class="label">Delivery price</div>
    <div class="amount">${fmtMoney(price)}</div>
    <div class="muted">Quote valid for 14 days from ${esc(date)}</div>
  </div>
  ${totalsBlock}

  <h2>Delivery Route</h2>
  <ul>
    <li><b>Pickup:</b> ${esc(pickup)}</li>
    ${stopRows || "<li>No delivery stops entered</li>"}
    <li><b>Estimated distance (round trip):</b> ${esc(effectiveMiles || 0)} miles</li>
    <li><b>Target pickup date:</b> ${esc(schedule?.pickupDate || "TBD")}</li>
    <li><b>Target delivery date:</b> ${esc(schedule?.deliveryDate || "TBD")}</li>
  </ul>

  ${splitBlock}
  ${etaBlock}

  <h2>Equipment</h2>
  <table>
    <thead><tr><th>#</th><th>Type</th><th>Brand</th><th>Model</th><th>Qty</th></tr></thead>
    <tbody>${unitRows || `<tr><td colspan="5" class="muted">No units entered</td></tr>`}</tbody>
  </table>

  <div class="footer">
    Questions about this quote? Contact ${
      salesAgent?.name
        ? `your K&amp;G sales agent <b>${esc(salesAgent.name)}</b>${salesAgent.email ? ` (${esc(salesAgent.email)})` : ""} or `
        : ""
    }<b>${esc(COMPANY.rfpEmail)}</b> &middot; ${esc(COMPANY.phone)}.<br/>
    Shipping method: ${esc(methodLabel || "K&G arranged delivery")}. Price includes standard loading and unloading; additional services may be quoted separately.
  </div>
</body></html>`;
}
